"use client"

import { Fragment, forwardRef, useMemo, type HTMLAttributes } from "react"
import { cn } from "../../lib/utils"

const DEFAULT_MARK_CLASS = "bg-primary text-primary-foreground"

function escapeRegex(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
}

export type HighlightedTextProps = Omit<
  HTMLAttributes<HTMLSpanElement>,
  "children"
> & {
  text: string
  highlight?: string
  markClassName?: string
}

export const HighlightedText = forwardRef<
  HTMLSpanElement,
  HighlightedTextProps
>(function HighlightedText(
  { text, highlight = "", className, markClassName, ...rest },
  ref
) {
  const resolvedHighlight = highlight.trim()
  const shouldHighlight =
    resolvedHighlight.length > 0 &&
    resolvedHighlight.length <= text.length &&
    text.toLowerCase().includes(resolvedHighlight.toLowerCase())

  const regex = useMemo(() => {
    if (!shouldHighlight) return null
    return new RegExp(`(${escapeRegex(resolvedHighlight)})`, "gi")
  }, [resolvedHighlight, shouldHighlight])

  const parts = useMemo(() => {
    if (!regex) return null
    return text.split(regex)
  }, [regex, text])

  if (!shouldHighlight || !parts) {
    return (
      <span ref={ref} className={cn(className)} {...rest}>
        {text}
      </span>
    )
  }

  const normalizedHighlight = resolvedHighlight.toLowerCase()

  return (
    <span ref={ref} className={cn(className)} {...rest}>
      {parts.map((part, index) =>
        part.toLowerCase() === normalizedHighlight ? (
          <mark
            key={`mark-${index}`}
            className={cn(DEFAULT_MARK_CLASS, markClassName)}
          >
            {part}
          </mark>
        ) : (
          <Fragment key={`text-${index}`}>{part}</Fragment>
        )
      )}
    </span>
  )
})
